<script setup>
import { ref, computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { WalletCards, RefreshCw, Search, CheckCircle2, AlertTriangle, Check, ArrowRight, X, Info, ChevronLeft, CreditCard } from '@lucide/vue';

const props = defineProps({
  category: { type: Object, required: true },
  ppobConfig: { type: Object, default: () => ({}) },
  formatRupiah: { type: Function, required: true },
});

// Isolated E-Wallet State
const selectedProduct = ref(null);
const phoneNumber = ref('');
const isChecking = ref(false);
const inquiryResult = ref(null);
const inquiryError = ref('');

const amountSelectionMode = ref('fixed'); // 'fixed' (Nominal Pilihan) or 'open' (Nominal Bebas)
const selectedSku = ref(null);
const customAmountRaw = ref('');
const customAmountError = ref('');

const reviewModalOpen = ref(false);
const isSubmitting = ref(false);
const purchaseResult = ref(null);
const resultModalOpen = ref(false);
const idempotencyKey = ref('');

const activeProducts = computed(() => {
  return (props.category?.products || []).filter(p => p.is_active);
});

function selectProduct(prod) {
  selectedProduct.value = prod;
  phoneNumber.value = '';
  inquiryResult.value = null;
  inquiryError.value = '';
  selectedSku.value = null;
  customAmountRaw.value = '';
  customAmountError.value = '';
  purchaseResult.value = null;
  amountSelectionMode.value = prod?.amount_mode === 'open' ? 'open' : 'fixed';
}

function resetProductSelection() {
  selectedProduct.value = null;
  phoneNumber.value = '';
  inquiryResult.value = null;
  inquiryError.value = '';
  selectedSku.value = null;
  customAmountRaw.value = '';
  customAmountError.value = '';
  purchaseResult.value = null;
  amountSelectionMode.value = 'fixed';
}

const isValidPhoneNumber = computed(() => {
  const cleaned = phoneNumber.value.replace(/\D/g, '');
  return cleaned.length >= 10 && cleaned.length <= 15;
});

function onPhoneInput(e) {
  let cleaned = e.target.value.replace(/\D/g, '');
  if (cleaned.startsWith('62')) {
    cleaned = '0' + cleaned.substring(2);
  }
  phoneNumber.value = cleaned;

  inquiryResult.value = null;
  selectedSku.value = null;
  customAmountRaw.value = '';
  customAmountError.value = '';
  inquiryError.value = '';
  purchaseResult.value = null;
}

async function runInquiry() {
  if (!isValidPhoneNumber.value || !selectedProduct.value || isChecking.value) return;

  isChecking.value = true;
  inquiryError.value = '';
  inquiryResult.value = null;
  selectedSku.value = null;
  customAmountRaw.value = '';
  customAmountError.value = '';

  try {
    const res = await axios.post(route('catalogue.ewallet.inquiry'), {
      master_product_id: selectedProduct.value.id,
      customer_input: {
        phone_number: phoneNumber.value,
      },
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      inquiryResult.value = res.data;
      if (selectedProduct.value?.amount_mode === 'open') {
        amountSelectionMode.value = 'open';
      } else {
        amountSelectionMode.value = 'fixed';
      }
    } else {
      if (phoneNumber.value.endsWith('999')) {
        inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
      } else if (phoneNumber.value.endsWith('000')) {
        inquiryError.value = 'Nomor akun tidak ditemukan.';
      } else {
        inquiryError.value = res.data.message || 'Pengecekan akun E-Wallet gagal.';
      }
    }
  } catch (err) {
    if (phoneNumber.value.endsWith('999')) {
      inquiryError.value = 'Layanan simulasi sedang mengalami timeout. Silakan coba kembali.';
    } else if (phoneNumber.value.endsWith('000')) {
      inquiryError.value = 'Nomor akun tidak ditemukan.';
    } else {
      inquiryError.value = err.response?.data?.message || 'Nomor akun tidak ditemukan atau inquiry gagal.';
    }
  } finally {
    isChecking.value = false;
  }
}

const availableSkus = computed(() => {
  if (!selectedProduct.value) return [];
  // Filter out internal open amount SKUs from fixed SKU grid
  return (selectedProduct.value.skus || []).filter(s => s.is_active && !s.code.endsWith('_OPEN_AMOUNT'));
});

const openAmountSku = computed(() => {
  if (!selectedProduct.value) return null;
  return (selectedProduct.value.skus || []).find(s => s.code.endsWith('_OPEN_AMOUNT')) || availableSkus.value[0];
});

const supportsFixedAmount = computed(() => {
  if (!selectedProduct.value) return false;
  return ['fixed', 'both'].includes(selectedProduct.value.amount_mode) || availableSkus.value.length > 0;
});

const supportsOpenAmount = computed(() => {
  if (!selectedProduct.value) return false;
  return ['open', 'both'].includes(selectedProduct.value.amount_mode);
});

const hasBothModes = computed(() => {
  return supportsFixedAmount.value && supportsOpenAmount.value;
});

function setAmountMode(mode) {
  amountSelectionMode.value = mode;
  if (mode === 'fixed') {
    customAmountRaw.value = '';
    customAmountError.value = '';
  } else if (mode === 'open') {
    selectedSku.value = null;
  }
}

const minOpenAmount = computed(() => selectedProduct.value?.minimum_open_amount_minor || 10000);
const maxOpenAmount = computed(() => selectedProduct.value?.maximum_open_amount_minor || 2000000);
const stepOpenAmount = computed(() => selectedProduct.value?.amount_step_minor || 1000);

const parsedCustomAmount = computed(() => {
  if (!customAmountRaw.value) return 0;
  const cleaned = customAmountRaw.value.replace(/\D/g, '');
  return parseInt(cleaned, 10) || 0;
});

function onCustomAmountInput(e) {
  const cleaned = e.target.value.replace(/\D/g, '');
  customAmountRaw.value = cleaned;
  selectedSku.value = null;
  validateCustomAmount();
}

function selectFixedSku(sku) {
  selectedSku.value = sku;
  customAmountRaw.value = '';
  customAmountError.value = '';
}

function validateCustomAmount() {
  if (!parsedCustomAmount.value) {
    customAmountError.value = '';
    return false;
  }

  const amt = parsedCustomAmount.value;
  if (amt < minOpenAmount.value) {
    customAmountError.value = `Nominal minimal ${props.formatRupiah(minOpenAmount.value)}.`;
    return false;
  }

  if (amt > maxOpenAmount.value) {
    customAmountError.value = `Nominal maksimal ${props.formatRupiah(maxOpenAmount.value)}.`;
    return false;
  }

  if (stepOpenAmount.value > 0 && (amt % stepOpenAmount.value !== 0)) {
    customAmountError.value = `Nominal harus dalam kelipatan ${props.formatRupiah(stepOpenAmount.value)}.`;
    return false;
  }

  customAmountError.value = '';
  return true;
}

const isSelectionValid = computed(() => {
  if (!inquiryResult.value) return false;
  if (amountSelectionMode.value === 'fixed') {
    return selectedSku.value !== null;
  }
  if (amountSelectionMode.value === 'open') {
    return supportsOpenAmount.value &&
           parsedCustomAmount.value >= minOpenAmount.value &&
           parsedCustomAmount.value <= maxOpenAmount.value &&
           (stepOpenAmount.value === 0 || parsedCustomAmount.value % stepOpenAmount.value === 0);
  }
  return false;
});

const activeSellingPrice = computed(() => {
  if (amountSelectionMode.value === 'fixed' && selectedSku.value) {
    return selectedSku.value.fallback_selling_price_minor;
  }
  if (amountSelectionMode.value === 'open' && parsedCustomAmount.value > 0) {
    return parsedCustomAmount.value;
  }
  return 0;
});

function openReviewModal() {
  if (!isSelectionValid.value) return;
  idempotencyKey.value = crypto.randomUUID();
  reviewModalOpen.value = true;
}

async function confirmPurchase() {
  if (!isSelectionValid.value || isSubmitting.value) return;

  isSubmitting.value = true;
  inquiryError.value = '';

  const targetSkuId = amountSelectionMode.value === 'fixed'
    ? selectedSku.value.id
    : openAmountSku.value?.id;

  try {
    const res = await axios.post(route('ewallet.mock-transactions.store'), {
      inquiry_reference: inquiryResult.value.inquiry_reference,
      master_sku_id: targetSkuId,
      custom_amount_minor: amountSelectionMode.value === 'open' ? parsedCustomAmount.value : null,
      idempotency_key: idempotencyKey.value,
    });

    if (res.data && (res.data.success || res.data.status === 'success')) {
      purchaseResult.value = res.data;
      reviewModalOpen.value = false;
      resultModalOpen.value = true;

      router.reload({ only: ['transactions'] });
    } else {
      inquiryError.value = res.data.message || 'Simulasi transaksi gagal.';
    }
  } catch (err) {
    inquiryError.value = err.response?.data?.message || 'Terjadi kesalahan saat simulasi transaksi.';
  } finally {
    isSubmitting.value = false;
  }
}

function resetFlow() {
  selectedProduct.value = null;
  phoneNumber.value = '';
  inquiryResult.value = null;
  selectedSku.value = null;
  customAmountRaw.value = '';
  customAmountError.value = '';
  inquiryError.value = '';
  purchaseResult.value = null;
  reviewModalOpen.value = false;
  resultModalOpen.value = false;
  amountSelectionMode.value = 'fixed';
}

function maskIdentifier(id) {
  if (!id) return '';
  if (id.length <= 6) return id;
  return id.substring(0, 4) + '****' + id.substring(id.length - 4);
}
</script>

<template>
  <div class="space-y-6">
    <!-- Step 1: Select E-Wallet Brand Cards First -->
    <div v-if="!selectedProduct" class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="font-bold text-sm text-slate-900">Pilih Layanan E-Wallet</h4>
          <p class="text-xs text-slate-500 mt-0.5">Pilih penyedia dompet digital tujuan transaksi</p>
        </div>
        <span v-if="ppobConfig.mock_mode" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase">MODE SIMULASI</span>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <button
          v-for="prod in activeProducts"
          :key="prod.id"
          @click="selectProduct(prod)"
          type="button"
          class="p-5 rounded-3xl border border-slate-200 bg-white hover:border-indigo-600 hover:bg-indigo-50/50 transition text-left flex flex-col justify-between space-y-3 shadow-2xs group cursor-pointer"
        >
          <div class="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-black group-hover:bg-indigo-600 group-hover:text-white transition">
            <WalletCards class="w-5 h-5" />
          </div>
          <div>
            <h5 class="font-bold text-xs text-slate-900 group-hover:text-indigo-950">{{ prod.name }}</h5>
            <p class="text-[10px] text-slate-500 mt-0.5 truncate">{{ prod.brand }}</p>
          </div>
        </button>
      </div>
    </div>

    <!-- Step 2: Destination Form & Amount Selection (Shown after selecting brand) -->
    <div v-else class="space-y-6">
      <div class="p-6 rounded-3xl bg-slate-50 border border-slate-200 space-y-4">
        <div class="flex items-center justify-between border-b border-slate-200/80 pb-3">
          <div class="flex items-center gap-2">
            <button @click="resetProductSelection" type="button" class="p-1 rounded-lg bg-white border border-slate-300 text-slate-600 hover:bg-slate-100 transition cursor-pointer">
              <ChevronLeft class="w-4 h-4" />
            </button>

            <div>
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">Top Up Saldo E-Wallet</span>
              <h4 class="font-black text-sm text-slate-900">{{ selectedProduct.name }}</h4>
            </div>
          </div>

          <span v-if="ppobConfig.mock_mode" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase">
            SIMULASI LOKAL
          </span>
        </div>

        <div class="space-y-3">
          <label for="ewallet_phone_input" class="block text-xs font-bold text-slate-700">
            Nomor HP Terdaftar {{ selectedProduct.name }} <span class="text-rose-500">*</span>
          </label>

          <div class="flex flex-col sm:flex-row gap-3">
            <div class="relative flex-1">
              <input
                id="ewallet_phone_input"
                :value="phoneNumber"
                @input="onPhoneInput"
                @keyup.enter.prevent="runInquiry"
                type="tel"
                placeholder="08xxxxxxxxxx"
                class="w-full px-4 py-3 min-h-[44px] rounded-2xl border border-slate-300 text-sm font-semibold tracking-wide bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20 transition outline-none"
              />
            </div>

            <button
              @click="runInquiry"
              :disabled="!isValidPhoneNumber || isChecking"
              type="button"
              class="px-6 py-3 min-h-[44px] rounded-2xl text-xs font-bold transition flex items-center justify-center gap-2 shadow-xs shrink-0 w-full sm:w-auto cursor-pointer"
              :class="isValidPhoneNumber && !isChecking
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'"
            >
              <RefreshCw v-if="isChecking" class="w-4 h-4 animate-spin" />
              <Search v-else class="w-4 h-4" />
              <span>{{ isChecking ? 'Mengecek...' : 'Cek Nomor' }}</span>
            </button>
          </div>

          <!-- Mock Account Inquiry Panel -->
          <div v-if="inquiryResult" class="p-4 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-900 space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <CheckCircle2 class="w-5 h-5 text-indigo-600 shrink-0" />
                <span class="font-bold text-xs">Akun {{ selectedProduct.name }} Ditemukan</span>
              </div>
              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200/80 text-amber-900 uppercase">
                SIMULASI LOKAL
              </span>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-white/80 p-3 rounded-xl border border-indigo-100 font-medium">
              <div>
                <span class="text-[10px] text-slate-500 block">Nomor Tujuan</span>
                <span class="font-mono font-bold text-slate-900">{{ inquiryResult.customer_number || inquiryResult.customer?.identifier }}</span>
              </div>
              <div>
                <span class="text-[10px] text-slate-500 block">Nama Akun</span>
                <span class="font-bold text-slate-900">{{ inquiryResult.customer_name || inquiryResult.customer?.name || 'PENGGUNA MOCK' }}</span>
              </div>
              <div>
                <span class="text-[10px] text-slate-500 block">Ref Inquiry</span>
                <span class="font-mono text-[10px] text-slate-600 truncate block">{{ inquiryResult.inquiry_reference }}</span>
              </div>
            </div>

            <p class="text-[11px] text-indigo-800/90 italic">
              * Data ini merupakan simulasi lokal dan bukan hasil pengecekan penyedia e-wallet.
            </p>
          </div>
        </div>
      </div>

      <!-- Denominations / Open Amount Selection Section -->
      <div v-if="inquiryResult" class="space-y-6">

        <!-- Mode Toggle / Segmented Control (Shown if product supports both) -->
        <div v-if="hasBothModes" class="flex p-1 bg-slate-100 rounded-2xl max-w-md">
          <button
            @click="setAmountMode('fixed')"
            type="button"
            class="flex-1 py-2 px-4 rounded-xl text-xs font-bold transition cursor-pointer"
            :class="amountSelectionMode === 'fixed'
              ? 'bg-white text-indigo-900 shadow-xs'
              : 'text-slate-600 hover:text-slate-900'"
          >
            Nominal Pilihan
          </button>
          <button
            @click="setAmountMode('open')"
            type="button"
            class="flex-1 py-2 px-4 rounded-xl text-xs font-bold transition cursor-pointer"
            :class="amountSelectionMode === 'open'
              ? 'bg-white text-indigo-900 shadow-xs'
              : 'text-slate-600 hover:text-slate-900'"
          >
            Nominal Bebas
          </button>
        </div>

        <!-- Mode A: Fixed Denominations Grid -->
        <div v-if="amountSelectionMode === 'fixed' && supportsFixedAmount" class="space-y-3">
          <h4 class="font-bold text-xs text-slate-900">Pilih Nominal Pilihan {{ selectedProduct.name }}</h4>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
              v-for="sku in availableSkus"
              :key="sku.id"
              @click="selectFixedSku(sku)"
              class="p-4 rounded-2xl border transition flex flex-col justify-between space-y-3 cursor-pointer relative"
              :class="selectedSku?.id === sku.id ? 'border-indigo-600 bg-indigo-50/90 ring-2 ring-indigo-500/20' : 'border-slate-200 bg-white hover:border-slate-300'"
            >
              <div>
                <div class="flex items-center justify-between">
                  <span class="font-mono text-[10px] font-bold text-slate-400">{{ sku.code }}</span>
                  <span class="text-[9px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Resmi</span>
                </div>
                <h5 class="font-bold text-xs text-slate-900 mt-1">{{ sku.name }}</h5>
                <p class="text-xs font-black text-indigo-600 mt-2">{{ formatRupiah(sku.fallback_selling_price_minor) }}</p>
              </div>

              <button
                type="button"
                class="w-full py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 cursor-pointer"
                :class="selectedSku?.id === sku.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'"
              >
                <Check v-if="selectedSku?.id === sku.id" class="w-3.5 h-3.5" />
                <span>{{ selectedSku?.id === sku.id ? 'Terpilih' : 'Pilih Nominal' }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Mode B: Open Amount Input Section -->
        <div v-else-if="amountSelectionMode === 'open' && supportsOpenAmount" class="p-6 rounded-3xl bg-white border border-slate-200 space-y-3 shadow-2xs">
          <div class="flex items-center justify-between">
            <label for="custom_amount_input" class="block text-xs font-bold text-slate-900">
              Nominal Top Up {{ selectedProduct.name }} (Nominal Bebas)
            </label>
            <span class="text-[10px] text-slate-500 font-medium">
              Min: {{ formatRupiah(minOpenAmount) }} • Max: {{ formatRupiah(maxOpenAmount) }}
            </span>
          </div>

          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-sm text-slate-400">Rp</span>
            <input
              id="custom_amount_input"
              :value="customAmountRaw"
              @input="onCustomAmountInput"
              type="text"
              inputmode="numeric"
              placeholder="Contoh: 50.000"
              class="w-full pl-12 pr-4 py-3 min-h-[44px] rounded-2xl border text-sm font-bold tracking-wide transition outline-none"
              :class="customAmountError
                ? 'border-rose-300 bg-rose-50/50 text-rose-900 focus:border-rose-500'
                : 'border-slate-300 bg-slate-50 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-500/20'"
            />
          </div>

          <p v-if="customAmountError" class="text-xs font-bold text-rose-600 flex items-center gap-1">
            <AlertTriangle class="w-3.5 h-3.5 shrink-0" />
            <span>{{ customAmountError }}</span>
          </p>
          <p v-else-if="parsedCustomAmount > 0" class="text-xs text-emerald-700 font-semibold flex items-center gap-1">
            <CheckCircle2 class="w-3.5 h-3.5 shrink-0" />
            <span>Nominal Bebas Terpilih: {{ formatRupiah(parsedCustomAmount) }}</span>
          </p>
        </div>

      </div>

      <!-- Error Alert -->
      <div v-if="inquiryError" class="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-center gap-2">
        <AlertTriangle class="w-4 h-4 text-rose-600 shrink-0" />
        <span>{{ inquiryError }}</span>
      </div>

      <!-- Action Trigger for Review Modal -->
      <div v-if="inquiryResult && isSelectionValid" class="p-5 rounded-2xl bg-indigo-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg">
        <div>
          <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">Top Up {{ selectedProduct.name }}</span>
          <span class="text-lg font-black text-amber-300">
            {{ amountSelectionMode === 'fixed' ? selectedSku?.name : (selectedProduct?.name + ' Nominal Bebas') }}
          </span>
          <p class="text-xs text-indigo-200 mt-0.5">Total Harga: {{ formatRupiah(activeSellingPrice) }}</p>
        </div>

        <button
          @click="openReviewModal"
          type="button"
          class="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
        >
          <span>Lanjutkan Simulasi Pembelian</span>
          <ArrowRight class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Purchase Review Modal -->
    <div v-if="reviewModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="flex items-center justify-between border-b border-slate-100 pb-4">
          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-[10px] uppercase">
              MODE SIMULASI
            </span>
            <h3 class="font-bold text-sm text-slate-900">Review Top Up {{ selectedProduct?.name }}</h3>
          </div>
          <button @click="reviewModalOpen = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="space-y-3 text-xs">
          <div class="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
            <div class="flex justify-between">
              <span class="text-slate-500">Penyedia E-Wallet:</span>
              <span class="font-bold text-slate-900">{{ selectedProduct?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Nomor HP Tujuan:</span>
              <span class="font-mono font-bold text-slate-900">{{ phoneNumber }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Nama Akun:</span>
              <span class="font-bold text-slate-900">{{ inquiryResult?.customer_name || inquiryResult?.customer?.name }}</span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500">Jenis Nominal:</span>
              <span class="font-bold text-indigo-600">
                {{ amountSelectionMode === 'fixed' ? 'Nominal Pilihan' : 'Nominal Bebas' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-slate-500">Nominal Top Up:</span>
              <span class="font-bold text-indigo-600">
                {{ amountSelectionMode === 'fixed' ? selectedSku?.name : formatRupiah(parsedCustomAmount) }}
              </span>
            </div>
            <div class="flex justify-between border-t border-slate-200 pt-2">
              <span class="text-slate-500 font-bold">Total Harga:</span>
              <span class="font-black text-slate-900 text-sm">{{ formatRupiah(activeSellingPrice) }}</span>
            </div>
          </div>

          <div class="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed flex items-start gap-2.5">
            <Info class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Saldo e-wallet tidak benar-benar bertambah.</span>
          </div>
        </div>

        <div class="flex items-center gap-3 pt-2">
          <button
            @click="reviewModalOpen = false"
            type="button"
            class="flex-1 py-3 rounded-2xl border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-50 transition cursor-pointer"
          >
            Kembali
          </button>
          <button
            @click="confirmPurchase"
            :disabled="isSubmitting"
            type="button"
            class="flex-1 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <RefreshCw v-if="isSubmitting" class="w-4 h-4 animate-spin" />
            <span>{{ isSubmitting ? 'Memproses...' : 'Konfirmasi Simulasi' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Purchase Result Modal -->
    <div v-if="resultModalOpen && purchaseResult" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-6 shadow-2xl border border-slate-100 relative">
        <div class="text-center space-y-2">
          <div class="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
            <CheckCircle2 class="w-7 h-7" />
          </div>
          <h3 class="text-lg font-black text-slate-900">Simulasi top up e-wallet berhasil</h3>
          <span class="inline-block px-3 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase">
            SIMULASI LOKAL
          </span>
        </div>

        <!-- Serial Number Card -->
        <div class="p-5 rounded-2xl bg-slate-900 text-white text-center space-y-2 shadow-md">
          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">NOMOR SERIAL SIMULASI</span>
          <div class="font-mono text-sm sm:text-base font-black text-amber-300 tracking-wider">
            {{ purchaseResult.serial_number }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-4 rounded-2xl border border-slate-200">
          <div>
            <span class="text-[10px] text-slate-500 block">No. Transaksi</span>
            <span class="font-mono font-bold text-slate-900 truncate block">{{ purchaseResult.transaction_reference }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">E-Wallet</span>
            <span class="font-bold text-indigo-600">{{ selectedProduct?.name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Nomor Tujuan</span>
            <span class="font-mono font-bold text-slate-900">{{ maskIdentifier(purchaseResult.customer_number) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Nama Akun</span>
            <span class="font-bold text-slate-900">{{ inquiryResult?.customer_name || inquiryResult?.customer?.name }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Total</span>
            <span class="font-bold text-slate-900">{{ formatRupiah(purchaseResult.selling_price_minor) }}</span>
          </div>
          <div>
            <span class="text-[10px] text-slate-500 block">Waktu</span>
            <span class="font-mono text-[10px] text-slate-600 block">{{ purchaseResult.completed_at || 'Baru saja' }}</span>
          </div>
        </div>

        <p class="text-[11px] text-slate-500 italic text-center">
          * Saldo e-wallet tidak benar-benar bertambah.
        </p>

        <button
          @click="resetFlow"
          type="button"
          class="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md transition cursor-pointer"
        >
          Transaksi Baru
        </button>
      </div>
    </div>
  </div>
</template>
